import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { redirect } from "next/navigation";
import { createClient } from "@/lib/supabase/server";
import { ContentFile } from "./types";


export function getVercelUrl() {
    return process.env.NEXT_PUBLIC_VERCEL_URL
}

/**
 * Redirects to a specified path with an encoded message as a query parameter.
 * @param {('error' | 'success')} type - The type of message, either 'error' or 'success'.
 * @param {string} path - The path to redirect to.
 * @param {string} message - The message to be encoded and added as a query parameter.
 * @returns {never} This function doesn't return as it triggers a redirect.
 */
export function encodedRedirect(
    type: "error" | "success",
    path: string,
    message: string,
) {
    return redirect(`${path}?${type}=${encodeURIComponent(message)}`);
}

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

// Tremor cx [v0.0.0]
export function cx(...args: ClassValue[]) {
    return twMerge(clsx(...args))
}

// Global consistent focus styles
export const focusStyles = [
    // Use outline-none to remove default browser focus styles
    "outline-none",
    // Use ring for consistent focus indicator
    "focus-visible:ring-2",
    "focus-visible:ring-ring",
    "focus-visible:ring-offset-2",
    "focus-visible:ring-offset-background",
]

// For input elements (form controls)
export const focusInputStyles = [
    "outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-ring",
    "focus-visible:border-ring",
]

// For error states
export const errorStyles = [
    "ring-2",
    "ring-destructive/20",
    "border-destructive",
]

// Legacy Tremor styles (keeping for backward compatibility)
// Tremor focusInput [v0.0.1] 
export const focusInput = focusInputStyles

// Tremor focusRing [v0.0.1]
export const focusRing = focusStyles

// Tremor hasErrorInput [v0.0.1]
export const hasErrorInput = errorStyles

export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B'
    
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
} 





export function isFileImage(file: ContentFile) {
    return file.mediaCategory === 'image' || getFileTypeGroup(file.extension || file.name.split('.').pop()?.toLowerCase() || '') === 'image'
}

// Smart rounding for user-friendly numbers used across pricing/usage displays

export function smartRound(value: number): number {
    if (value < 10) return value
    if (value < 100) return Math.round(value / 5) * 5
    if (value < 1000) return Math.round(value / 10) * 10
    if (value < 10000) return Math.round(value / 100) * 100
    return Math.round(value / 1000) * 1000
}


// File type groupings for display purposes
export const fileTypeGroups = {
    image: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.tif', '.ico', '.heic'],
    document: ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.md', '.odt', '.html'],
    spreadsheet: ['.xls', '.xlsx', '.csv', '.ods', '.numbers'],
    presentation: ['.ppt', '.pptx', '.key', '.odp'],
    code: ['.js', '.jsx', '.ts', '.tsx', '.css', '.py', '.java', '.c', '.cpp', '.cs', '.php', '.rb', '.go', '.rs', '.swift', '.kt', '.sql', '.vue', '.json', '.xml', '.yaml', '.yml'],
    archive: ['.zip', '.rar', '.7z', '.tar', '.gz'],
    audio: ['.mp3', '.wav', '.ogg', '.flac', '.aac', '.m4a'],
    video: ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.flv', '.wmv'],
    font: ['.ttf', '.otf', '.woff', '.woff2'],
    ebook: ['.epub', '.mobi', '.azw'],
    design: ['.psd', '.ai', '.xd', '.sketch', '.fig'],
    model: ['.obj', '.fbx', '.blend', '.stl', '.glb', '.gltf'],
}

// Get file type group based on extension
export const getFileTypeGroup = (extension: string): string => {
    const ext = extension.startsWith('.') ? extension : `.${extension}`
    
    for (const [group, extensions] of Object.entries(fileTypeGroups)) {
        if (extensions.includes(ext.toLowerCase())) {
            return group
        }
    }
    
    return 'other'
}